import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DefaultButton } from '../Buttons'
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import { useLanguage } from './../../context/LanguageContext';

const DisagreeBtn = withStyles({
  root: {
    color: 'white',
    backgroundColor: '#ADADAD',
    borderColor: '#ADADAD',
    "&:hover": {
      backgroundColor: "#ADADAD"
    }
  },
})(DefaultButton);

const AgreeBtn = withStyles({
  root: {
    color: 'white',
    backgroundColor: '#009688',
    borderColor: '#009688',
    "&:hover": {
      backgroundColor: "#009688"
    }
  },
})(DefaultButton);

const AlertDialog = props => {
  const { labels } = useLanguage();
  const ALERT_DIALOG = labels?.ALERT_DIALOG || {};
  
  // Constants
  const TRANSITION_DURATION = 600;
  const TRANSITION_STYLE = 'all 0.6s ease-in-out';
  
  // Determine if this dialog is in background (inactive) mode
  const isBackground = props.isBackground || false;
  const isFadingOut = props.isFadingOut || false;
  const zIndex = props.style?.zIndex || (isBackground ? 9998 : 9999);


  const getDialogWidth = () => {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1366;

    if (screenWidth < 600) {
      return { maxWidth: '95vw', width: 'calc(100% - 32px)' };
    } else if (screenWidth < 960) {
      return { maxWidth: '90vw', width: 'calc(100% - 80px)' };
    } else if (screenWidth <= 1366) {
      // HP laptop and similar resolutions
      return { maxWidth: '85vw', width: '800px' };
    } else if (screenWidth <= 1600) {
      return { maxWidth: '80vw', width: '900px' };
    }
    return { maxWidth: '70vw', width: '950px' };
  };

  const [dialogWidth, setDialogWidth] = useState(getDialogWidth());

  useEffect(() => {
    const handleResize = () => setDialogWidth(getDialogWidth());
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Base transition properties
  const baseTransition = {
    transition: TRANSITION_STYLE,
    opacity: isFadingOut ? 0 : 1
  };

  const isSingleModal = !isBackground && !props.hasOtherModals;

  const paperStyle = isBackground ? {
    backgroundColor: 'rgba(240, 240, 240, 0.95)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)', 
    ...dialogWidth,
    maxHeight: '85vh',
    overflowY: 'auto',
    marginTop: '300px',
    ...baseTransition
  } : {
      ...dialogWidth,
      maxHeight: '85vh',
      overflowY: 'auto',
      marginTop: isSingleModal ? '0px' : '-200px',
    ...baseTransition
  };

  const contentOpacity = isBackground ? 0.5 : 1;
  const contentStyle = {
    opacity: contentOpacity,
    transition: 'opacity 0.6s ease-in-out'
  };

  // Backdrop style - transparent when in background to show both dialogs
  const backdropStyle = {
    backgroundColor: isBackground ? 'rgba(0, 0, 0, 0)' : 'rgba(0, 0, 0, 0.5)',
    transition: 'background-color 0.6s ease-in-out'
  };

  return (
    <div>
      <Dialog
        open={props.openAlertDialog}
        onClose={isBackground ? undefined : props.handleCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={false} // Disable default maxWidth to use our custom width
        fullWidth={false} // Disable fullWidth to use our custom width
        disableEscapeKeyDown={isBackground}
        transitionDuration={TRANSITION_DURATION}
        style={{
          zIndex,
          pointerEvents: isBackground ? 'none' : 'auto',
          ...props.style
        }}
        BackdropProps={{
          style: backdropStyle,
        }}
        PaperProps={{ style: paperStyle }}
      >
        <DialogTitle id="alert-dialog-title" style={contentStyle}>
          {props.alertDialogTitle || ALERT_DIALOG.WARNING}
        </DialogTitle>
        <DialogContent style={contentStyle}>
          <DialogContentText id="alert-dialog-description">
            {props.alertDialogMessage}
          </DialogContentText>
          {props.children}
        </DialogContent>
        <DialogActions style={contentStyle}>
          {props.cancelButtonLabel && (
            <DisagreeBtn onClick={props.handleCancel} color="primary">
              {props.cancelButtonLabel || ALERT_DIALOG.DISAGREE}
            </DisagreeBtn>
          )}
          <AgreeBtn
            onClick={props.handleOk}
            color="primary"
            autoFocus={!props.cancelButtonLabel}
            disabled={props.disabled}
          >
            {props.okButtonLabel || ALERT_DIALOG.AGREE}
          </AgreeBtn>
        </DialogActions>
      </Dialog>
    </div>
  );

}

AlertDialog.propTypes = {
  openAlertDialog: PropTypes.bool.isRequired,
  alertDialogMessage: PropTypes.string.isRequired,
  alertDialogTitle: PropTypes.string.isRequired,
  handleCancel: PropTypes.func,
  handleOk: PropTypes.func,
  okButtonLabel: PropTypes.string,
  cancelButtonLabel: PropTypes.string,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  isBackground: PropTypes.bool,
  isFadingOut: PropTypes.bool,
  style: PropTypes.object,
  hasOtherModals: PropTypes.bool
};

AlertDialog.defaultProps = {
  openAlertDialog: false,
  alertDialogMessage: '',
  alertDialogTitle: '',
  okButtonLabel: '',
  cancelButtonLabel: '',
  disabled: false,
  handleCancel: () => { },
  handleOk: () => { },
  children: null,
  isBackground: false,
  isFadingOut: false,
  style: {},
  hasOtherModals: false
};

export default AlertDialog;
